import React from 'react';
import { useCSVDownloader } from 'react-papaparse';
import DataGrid from '../../UI/DataGrid/DataGrid';
import S from './SensorDataTab.module.scss';
import { useSensorDataTab } from './useSensorDataTab';
import SensorHistoryPopup from './SensorHistoryPopup';
import SensorImportTab from './SensorImportTab';
import SensorChartTab from './SensorChartTab';

const SensorDataTab = () => {
  const { CSVDownloader } = useCSVDownloader();
  const [activeTab, setActiveTab] = React.useState('data');
  const [sensorSearch, setSensorSearch] = React.useState('');
  const [showChart, setShowChart] = React.useState(false);
  const [chartSensorKey, setChartSensorKey] = React.useState('');

  const {
    capteurs,
    loadingCapteurs,
    sensorPositionMap,
    toggleSensor,
    togglePosition,
    hasSelection,
    selectionSummary,
    startDateTime,
    setStartDateTime,
    endDateTime,
    setEndDateTime,
    nValues,
    setNValues,
    rows,
    rowSelectionModel,
    handleRowSelectionModelChange,
    paginationModel,
    setPaginationModel,
    loadingData,
    saving,
    error,
    fetched,
    columns,
    dirtyCount,
    historyMap,
    fetchData,
    handleBulkDelete,
    handleSaveAll,
    handleProcessRowUpdate,
    handleProcessRowUpdateError,
    historyPopupData,
    handleCloseHistory,
    getSensorNameById,
  } = useSensorDataTab();

  const sensorKeys = React.useMemo(() => {
    const seen = new Set();
    const keys = [];
    rows.forEach((r) => {
      const k = `${r._sensorId}_${r.position_in_sensor}`;
      if (!seen.has(k)) {
        seen.add(k);
        keys.push({
          key: k,
          label: `${getSensorNameById(Number(r._sensorId))} — Pos. ${r.position_in_sensor}`,
        });
      }
    });
    return keys;
  }, [rows, getSensorNameById]);

  React.useEffect(() => {
    if (sensorKeys.length > 0) setChartSensorKey(sensorKeys[0].key);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartRows = React.useMemo(
    () =>
      chartSensorKey
        ? rows.filter(
            (r) => `${r._sensorId}_${r.position_in_sensor}` === chartSensorKey
          )
        : rows,
    [rows, chartSensorKey]
  );

  const filteredCapteurs = React.useMemo(() => {
    const q = sensorSearch.trim().toLowerCase();
    if (!q) return capteurs;
    return capteurs.filter((c) => {
      const sId = String(c.sensor_id ?? c.id ?? '').toLowerCase();
      const sName = String(c.sensor_name ?? c.name ?? '').toLowerCase();
      return sId.includes(q) || sName.includes(q);
    });
  }, [capteurs, sensorSearch]);

  if (loadingCapteurs) {
    return (
      <div className={S.loadingState}>
        <div className={S.loadingCard}>
          <div className={S.loadingSpinner} />
          <p>Chargement de la liste des capteurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={S.sensorDataTab}>
      <div className={S.content}>
        <div className={S.tabBar}>
          <button
            type="button"
            className={[S.tabButton, activeTab === 'data' ? S.tabButtonActive : ''].join(' ')}
            onClick={() => setActiveTab('data')}
          >
            Données
          </button>
          <button
            type="button"
            className={[S.tabButton, activeTab === 'import' ? S.tabButtonActive : ''].join(' ')}
            onClick={() => setActiveTab('import')}
          >
            Import CSV
          </button>
        </div>

        {activeTab === 'import' ? (
          <SensorImportTab />
        ) : (
          <>
            {/* ── Selector bar + chart side by side ── */}
            <div className={S.topPanel}>
              {/* LEFT: filter controls */}
              <div className={S.sensorSelectorBar}>
                <div className={S.selectorGroup}>
                  <span className={S.selectorLabel}>Capteurs et positions</span>
                  <input
                    type="text"
                    className={S.sensorSearchInput}
                    value={sensorSearch}
                    onChange={(e) => setSensorSearch(e.target.value)}
                    placeholder="Rechercher par nom ou ID"
                  />
                  <div className={S.capteurCheckList}>
                    {!capteurs.length && (
                      <span className={S.noSensor}>Aucun capteur disponible</span>
                    )}
                    {!!capteurs.length && !filteredCapteurs.length && (
                      <span className={S.noSensor}>Aucun capteur trouvé</span>
                    )}
                    {filteredCapteurs.map((c) => {
                      const sId = String(c.sensor_id ?? c.id ?? '');
                      const isChecked = !!sensorPositionMap[sId];
                      const selectedPositions = sensorPositionMap[sId] ?? [];
                      const sensorName = c.sensor_name ?? c.name ?? `Capteur ${sId}`;
                      return (
                        <div key={sId} className={S.capteurCheckItem}>
                          <label className={S.capteurCheckLabel}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSensor(sId)}
                            />
                            <span>{sensorName} (ID: {sId})</span>
                          </label>
                          {isChecked && (
                            <div className={S.positionCheckList}>
                              {[1, 2, 3].map((pos) => (
                                <label key={pos} className={S.positionCheckLabel}>
                                  <input
                                    type="checkbox"
                                    checked={selectedPositions.includes(pos)}
                                    onChange={() => togglePosition(sId, pos)}
                                  />
                                  <span>Pos. {pos}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={S.selectorGroup}>
                  <label className={S.selectorLabel} htmlFor="sd-date-start">
                    Date début
                  </label>
                  <div className={S.inputWithClear}>
                    <input
                      id="sd-date-start"
                      type="datetime-local"
                      className={S.dateInput}
                      value={startDateTime}
                      onChange={(e) => setStartDateTime(e.target.value)}
                    />
                    {startDateTime && (
                      <button
                        type="button"
                        className={S.clearInputButton}
                        title="Vider la date de début"
                        onClick={() => setStartDateTime('')}
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>

                <div className={S.selectorGroup}>
                  <label className={S.selectorLabel} htmlFor="sd-date-end">
                    Date fin
                  </label>
                  <div className={S.inputWithClear}>
                    <input
                      id="sd-date-end"
                      type="datetime-local"
                      className={S.dateInput}
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(e.target.value)}
                    />
                    {endDateTime && (
                      <button
                        type="button"
                        className={S.clearInputButton}
                        title="Vider la date de fin"
                        onClick={() => setEndDateTime('')}
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>

                <div className={S.selectorGroup}>
                  <label className={S.selectorLabel} htmlFor="sd-n-values">
                    N derniers points
                  </label>
                  <div className={S.inputWithClear}>
                    <input
                      id="sd-n-values"
                      type="number"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      className={S.nValuesInput}
                      value={nValues}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === '' || /^[1-9]\d*$/.test(next)) setNValues(next);
                      }}
                    />
                    {nValues && (
                      <button
                        type="button"
                        className={S.clearInputButton}
                        title="Vider N derniers points"
                        onClick={() => setNValues('')}
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>

                <button
                  className={S.btnFetch}
                  onClick={fetchData}
                  disabled={!hasSelection || loadingData}
                >
                  {loadingData ? 'Chargement…' : 'Charger les données'}
                </button>
              </div>

              {/* RIGHT: chart area (only when showChart + data loaded) */}
              {showChart && fetched && rows.length > 0 && (
                <div className={S.selectorChartArea}>
                  <SensorChartTab
                    rows={chartRows}
                    historyMap={historyMap}
                    getSensorNameById={getSensorNameById}
                  />
                </div>
              )}
            </div>

            {error && <div className={S.alertErreur}>{error}</div>}

            {fetched && (
              <div className={S.sensorGridWrapper}>
                <div className={S.sectionHeader}>
                  <div className={S.sectionHeaderLeft}>
                    <button
                      type="button"
                      className={showChart ? S.btnChartActive : S.btnChart}
                      onClick={() => setShowChart((v) => !v)}
                      title={showChart ? 'Masquer le graphique' : 'Tracer le graphique X-T'}
                    >
                      Visualiser
                    </button>
                    <span className={S.sectionHeaderTitle}>
                      {selectionSummary || 'Données capteur'}
                    </span>
                    {showChart && sensorKeys.length > 1 && (
                      <select
                        className={S.chartSensorSelect}
                        value={chartSensorKey}
                        onChange={(e) => setChartSensorKey(e.target.value)}
                      >
                        {sensorKeys.map((sk) => (
                          <option key={sk.key} value={sk.key}>
                            {sk.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className={S.sectionHeaderRight}>
                    <span className={S.badgeOk}>{rows.length} ligne(s)</span>
                    <CSVDownloader
                      data={rows.map(
                        ({ id, _dirty, _sensorId, old_time_unix, old_value, old_calc_value, ...rest }) =>
                          ({ sensor_id: _sensorId, ...rest })
                      )}
                      filename={`export_capteur_${new Date().toISOString().slice(0, 10)}`}
                      bom
                      config={{ delimiter: ';' }}
                      className={S.btnExport}
                    >
                      Exporter CSV
                    </CSVDownloader>
                  </div>
                </div>

                <div className={S.gridBody}>
                  <DataGrid
                    className={S.sensorDataGrid}
                    rows={rows}
                    columns={columns}
                    loading={loadingData}
                    autoHeight
                    checkboxSelection
                    checkboxSelectionVisibleOnly
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    editMode="row"
                    processRowUpdate={handleProcessRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    pageSizeOptions={[25, 50, 100, { value: -1, label: 'All' }]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25 } },
                      sorting: { sortModel: [{ field: 'time_unix', sort: 'asc' }] },
                    }}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => (params.row._dirty ? S.rowDirty : '')}
                  />
                </div>

                <div className={S.sensorGridFooter}>
                  <button
                    className={S.btnBulkDelete}
                    onClick={handleBulkDelete}
                    disabled={!rowSelectionModel.length || saving || loadingData}
                  >
                    Supprimer la sélection ({rowSelectionModel.length})
                  </button>
                  {dirtyCount > 0 ? (
                    <span className={S.badgeModifie}>
                      {dirtyCount} ligne(s) non sauvegardée(s)
                    </span>
                  ) : (
                    <span className={S.badgeSynchro}>
                      Toutes les modifications sont sauvegardées
                    </span>
                  )}
                  <button
                    className={S.btnSaveData}
                    onClick={handleSaveAll}
                    disabled={saving || dirtyCount === 0}
                  >
                    {saving ? 'Enregistrement…' : `Enregistrer (${dirtyCount})`}
                  </button>
                </div>
              </div>
            )}

            {fetched && rows.length === 0 && !loadingData && (
              <p className={S.emptyState}>
                Aucune donnée trouvée pour la sélection en cours.
              </p>
            )}
          </>
        )}
      </div>

      {historyPopupData && (
        <SensorHistoryPopup
          row={historyPopupData.row}
          entries={historyPopupData.entries}
          sensorName={getSensorNameById(historyPopupData.row?._sensorId)}
          onClose={handleCloseHistory}
        />
      )}
    </div>
  );
};

export default SensorDataTab;
